import makeApiCall from "./master/axiosIndex";

/**
 * Asset Master — machinery / tools / equipment as assets (Assets → Asset
 * Master). assetCode is generated server-side and immutable; status changes go
 * through changeStatus (never update) so assignment fields can't drift.
 *
 * DOCUMENTS/PHOTO do not pass through these endpoints. They go straight to DMS:
 * upload via documentApis.uploadDocument (dms/upload-multi) with
 * linkType 'asset' + linkTo assetId, list via documentApis.listDocuments
 * (dms/docsListPagination) with modules=asset&linkToFilters=<assetId>.
 *
 * Decimals travel as strings; dates as ISO strings — parse on the consumer side.
 */

export type AssetStatus = "Available" | "Assigned" | "UnderMaintenance" | "Retired";

export interface AssetPayload {
  assetId?: number;
  assetName?: string;
  toolTypeId?: number | null;
  category?: string;
  subCategory?: string;

  projectId?: number | null;
  location?: string;

  serialNumber?: string;
  purchaseDate?: string;
  purchaseCost?: number;
  poNumber?: string;
  ownership?: "Owned" | "Hired";
  notes?: string;

  manufacturer?: string;
  model?: string;
  vendorName?: string;
  vendorContact?: string;
  manufacturerRemarks?: string;

  capacity?: string;
  capacityUomId?: number | null;
  bhpRating?: number;
  powerKw?: number;
  fuelType?: "Diesel" | "Electric" | "Both" | "None";
  yearOfManufacture?: number;
  registrationNumber?: string;
  engineNumber?: string;
  chassisNumber?: string;
  technicalRemarks?: string;

  maintenanceDueDate?: string;
  warrantyExpiryDate?: string;
  insuranceExpiryDate?: string;
  lifecycleExpiryDate?: string;
}

export const assetApis = {
  create: (data: AssetPayload): Promise<any> =>
    makeApiCall({ url: "/assets/create", method: "post", data }),

  update: (data: AssetPayload & { assetId: number }): Promise<any> =>
    makeApiCall({ url: "/assets/update", method: "put", data }),

  /**
   * The ONE status write path. Assigned requires assignedToUserId (+ optional
   * project/return date/location); every other status clears the assignment.
   */
  changeStatus: (data: {
    assetId: number;
    status: AssetStatus;
    assignedToUserId?: number;
    projectId?: number;
    returnDate?: string;
    location?: string;
    remarks?: string;
  }): Promise<any> =>
    makeApiCall({ url: "/assets/status", method: "patch", data }),

  getById: (assetId: number): Promise<any> =>
    makeApiCall({ url: `/assets/id/${assetId}`, method: "get" }),

  getList: (params: any): Promise<any> =>
    makeApiCall({ url: "/assets/list", method: "get", params }),

  /** Light picker feed (excludes Retired) — norm equipment lines consume this. */
  getOptions: (projectId?: number): Promise<any> =>
    makeApiCall({
      url: "/assets/options",
      method: "get",
      params: projectId ? { projectId } : {},
    }),
};

export default assetApis;
